import React, { useState } from 'react';
import { FaFileCode } from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import { useSession } from '../Utils/SessionContext';
import { useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import RawJsonEditor from '../Utils/RawJsonEditor';

// Helper component for displaying skill items
const SkillItem = ({ skill }) => {
  if (!skill) return null;
  
  // Handle case where skill is a string
  if (typeof skill === 'string') {
    return (
      <div className="bg-gray-50 p-3 rounded">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="font-medium text-gray-800">{skill}</h5>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`bg-gray-50 p-3 rounded ${
      skill.importance === 'Nice To Have' 
        ? 'border-l-4 border-blue-300' 
        : 'border-l-4 border-red-300'
    }`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h5 className="font-medium text-gray-800">{skill.skill || skill.name}</h5>
          {skill.description && <p className="text-gray-600 text-sm mt-1">{skill.description}</p>}
          {/* Show context as bullet points */}
          {skill.context && Array.isArray(skill.context) && skill.context.length > 0 && (
            <div className="mt-2">
              <ul className="text-gray-600 text-xs space-y-1">
                {skill.context.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-500 mr-2 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {skill.idealYears && (
          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-4 py-1 rounded text-center whitespace-nowrap ml-3">
            Min {skill.idealYears} {parseInt(skill.idealYears) === 1 ? 'year' : 'years'}
          </span>
        )}
      </div>
    </div>
  );
};

// Component for job requirements content to avoid duplication
const JobRequirementsContent = ({ jobRequirement }) => (
  <div className="space-y-4">
    {/* Header with job title */}
    <div className="flex justify-between items-center bg-white p-5 rounded-lg shadow-sm border border-gray-200">
      {/* jobRequirement.jobTitle is BACKWARDS COMPATIBILITY IN THE JSON */}
      <h2 className="text-2xl font-bold text-gray-800">{jobRequirement.jobTitle.title || jobRequirement.jobTitle}</h2>
    </div>
    
    {/* Company and Seniority Level in separate boxes */}
    <div className="flex flex-col md:flex-row gap-4">
      {/* Company Box - 67% width */}
      {jobRequirement.company && (
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 md:w-2/3">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 pb-1 border-b border-gray-200">Company</h3>
          <p className="text-gray-600">{jobRequirement.company.content}</p>
        </div>
      )}
      
      {/* Seniority Level Box - 33% width */}
      {jobRequirement.seniority && (
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 md:w-1/3">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 pb-1 border-b border-gray-200">Required Seniority Level</h3>
          <p className="text-gray-600">{jobRequirement.seniority.level}</p>
        </div>
      )}
    </div>

    {/* Job Description Section */}
    {jobRequirement.description && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Job Description</h3>
        </div>
        <p className="text-gray-600 whitespace-pre-line">{jobRequirement.description.content}</p>
      </div>
    )}

    {/* Ideal Candidate Section */}
    {jobRequirement.idealCandidate && jobRequirement.idealCandidate.candidate && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Ideal Candidate</h3>
        </div>
        
        {/* Summary */}
        {jobRequirement.idealCandidate.candidate.summary && (
          <div className="mb-4">
            <p className="text-gray-600">{jobRequirement.idealCandidate.candidate.summary}</p>
          </div>
        )}
        
        {/* Particularities */}
        {jobRequirement.idealCandidate.candidate.particularities && 
         jobRequirement.idealCandidate.candidate.particularities.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-md font-semibold text-blue-700 mb-2">Key Attributes</h4>
            {jobRequirement.idealCandidate.candidate.particularities.map((item, index) => (
              <div key={index} className="flex items-start py-1">
                <span className="text-blue-500 mr-2">•</span>
                <span className="text-gray-600">{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    {/* Hard Skills Section */}
    {jobRequirement.hardSkills && jobRequirement.hardSkills.value && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Hard Skills</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Required Hard Skills */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Required</h4>
            <div className="space-y-2">
              {Array.isArray(jobRequirement.hardSkills.value) && 
                jobRequirement.hardSkills.value
                  .filter(skill => skill.importance === 'Required')
                  .map((skill, index) => (
                    <SkillItem key={index} skill={skill} />
                  ))
              }
              {Array.isArray(jobRequirement.hardSkills.value) && 
                jobRequirement.hardSkills.value.filter(skill => skill.importance === 'Required').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No required hard skills specified
                </div>
              )}
            </div>
          </div>
          
          {/* Nice to Have Hard Skills */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Nice to Have</h4>
            <div className="space-y-2">
              {Array.isArray(jobRequirement.hardSkills.value) && 
                jobRequirement.hardSkills.value
                  .filter(skill => skill.importance === 'Nice To Have')
                  .map((skill, index) => (
                    <SkillItem key={index} skill={skill} />
                  ))
              }
              {Array.isArray(jobRequirement.hardSkills.value) && 
                jobRequirement.hardSkills.value.filter(skill => skill.importance === 'Nice To Have').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No nice-to-have hard skills specified
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Soft Skills Section */}
    {jobRequirement.softSkills && jobRequirement.softSkills.value && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Soft Skills</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Required Soft Skills */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Required</h4>
            <div className="space-y-2">
              {Array.isArray(jobRequirement.softSkills.value) && 
                jobRequirement.softSkills.value
                  .filter(skill => skill.importance === 'Required')
                  .map((skill, index) => (
                    <SkillItem key={index} skill={skill} />
                  ))
              }
              {Array.isArray(jobRequirement.softSkills.value) && 
                jobRequirement.softSkills.value.filter(skill => skill.importance === 'Required').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No required soft skills specified
                </div>
              )}
            </div>
          </div>
          
          {/* Nice to Have Soft Skills */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Nice to Have</h4>
            <div className="space-y-2">
              {Array.isArray(jobRequirement.softSkills.value) && 
                jobRequirement.softSkills.value
                  .filter(skill => skill.importance === 'Nice To Have')
                  .map((skill, index) => (
                    <SkillItem key={index} skill={skill} />
                  ))
              }
              {Array.isArray(jobRequirement.softSkills.value) && 
                jobRequirement.softSkills.value.filter(skill => skill.importance === 'Nice To Have').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No nice-to-have soft skills specified
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Expertise Section */}
    {jobRequirement.expertise && jobRequirement.expertise.value && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Expertise</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Required Expertise */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Required</h4>
            <div className="space-y-2">
              {jobRequirement.expertise.value
                .filter(exp => exp.importance === 'Required')
                .map((exp, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded border-l-4 border-red-300">
                    <h5 className="font-medium text-gray-800">{exp.expertise}</h5>
                    {/* Show context as bullet points */}
                    {exp.context && Array.isArray(exp.context) && exp.context.length > 0 && (
                      <div className="mt-2">
                        <ul className="text-gray-600 text-xs space-y-1">
                          {exp.context.map((item, contextIndex) => (
                            <li key={contextIndex} className="flex items-start">
                              <span className="text-blue-500 mr-2 mt-0.5">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              {jobRequirement.expertise.value.filter(exp => exp.importance === 'Required').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No required expertise specified
                </div>
              )}
            </div>
          </div>
          
          {/* Nice to Have Expertise */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Nice to Have</h4>
            <div className="space-y-2">
              {jobRequirement.expertise.value
                .filter(exp => exp.importance === 'Nice To Have')
                .map((exp, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded border-l-4 border-blue-300">
                    <h5 className="font-medium text-gray-800">{exp.expertise}</h5>
                    {/* Show context as bullet points */}
                    {exp.context && Array.isArray(exp.context) && exp.context.length > 0 && (
                      <div className="mt-2">
                        <ul className="text-gray-600 text-xs space-y-1">
                          {exp.context.map((item, contextIndex) => (
                            <li key={contextIndex} className="flex items-start">
                              <span className="text-blue-500 mr-2 mt-0.5">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              {jobRequirement.expertise.value.filter(exp => exp.importance === 'Nice To Have').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No nice-to-have expertise specified
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Candidate Requirements Section */}
    {jobRequirement.candidateRequirements && jobRequirement.candidateRequirements.value && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Candidate Requirements</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Required Candidate Requirements */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Required</h4>
            <div className="space-y-2">
              {jobRequirement.candidateRequirements.value
                .filter(req => req.importance === 'Required')
                .map((req, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded border-l-4 border-red-300">
                    <p className="text-gray-700">{req.requirement}</p>
                  </div>
                ))}
              {jobRequirement.candidateRequirements.value.filter(req => req.importance === 'Required').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No required candidate requirements specified
                </div>
              )}
            </div>
          </div>
          
          {/* Nice to Have Candidate Requirements */}
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Nice to Have</h4>
            <div className="space-y-2">
              {jobRequirement.candidateRequirements.value
                .filter(req => req.importance === 'Nice To Have')
                .map((req, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded border-l-4 border-blue-300">
                    <p className="text-gray-700">{req.requirement}</p>
                  </div>
                ))}
              {jobRequirement.candidateRequirements.value.filter(req => req.importance === 'Nice To Have').length === 0 && (
                <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
                  No nice-to-have candidate requirements specified
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Responsibilities Section */}
    {jobRequirement.responsibilities && jobRequirement.responsibilities.responsibilityList && (
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Responsibilities</h3>
        </div>
        <div className="space-y-1">
          {jobRequirement.responsibilities.responsibilityList.map((responsibility, index) => (
            <div key={index} className="flex items-start py-1">
              <span className="text-blue-500 mr-2">•</span>
              <span className="text-gray-600">{responsibility}</span>
            </div>
          ))}
          {jobRequirement.responsibilities.responsibilityList.length === 0 && (
            <div className="bg-gray-50 p-3 rounded text-gray-500 italic">
              No responsibilities specified
            </div>
          )}
        </div>
      </div>
    )}

    {/* Additional Information Section */}
    <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center mb-3 pb-1 border-b border-gray-200">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-800">Additional Information</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Mindset */}
        {jobRequirement.mindset && (
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Working Mindset</h4>
            <p className="text-gray-600">{jobRequirement.mindset.content}</p>
          </div>
        )}
        
        {/* Contribution */}
        {jobRequirement.contribution && (
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Contribution Style</h4>
            <p className="text-gray-600">{jobRequirement.contribution.content}</p>
          </div>
        )}
        
        {/* Locations */}
        {jobRequirement.locations && (
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Location</h4>
            <p className="text-gray-600">
              {jobRequirement.locations.locations && jobRequirement.locations.locations.length > 0
                ? jobRequirement.locations.locations.join(', ')
                : 'Not specified'}
            </p>
          </div>
        )}
        
        {/* Languages */}
        {jobRequirement.languages && (
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Required Languages</h4>
            <p className="text-gray-600">
              {jobRequirement.languages.languages && jobRequirement.languages.languages.length > 0
                ? jobRequirement.languages.languages.join(', ')
                : 'Not specified'}
            </p>
          </div>
        )}
        
        {/* Education */}
        {jobRequirement.educations && (
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Education Requirements</h4>
            <p className="text-gray-600">
              {jobRequirement.educations.educations && jobRequirement.educations.educations.length > 0
                ? jobRequirement.educations.educations.join(', ')
                : 'Not specified'}
            </p>
          </div>
        )}
        
        {/* Certifications */}
        {jobRequirement.certifications && (
          <div>
            <h4 className="text-md font-semibold text-blue-700 mb-2">Required Certifications</h4>
            <p className="text-gray-600">
              {jobRequirement.certifications.certifications && jobRequirement.certifications.certifications.length > 0
                ? jobRequirement.certifications.certifications.join(', ')
                : 'Not specified'}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const JobRequirements = ({ jobRequirement, jdPDF }) => {
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const [isRawEditOpen, setIsRawEditOpen] = useState(false);
  
  // Get job ID from URL parameter
  const jobId = searchParams.get('jid');

  // Save function for job requirements
  const handleSaveJobRequirements = async (editedData) => {
    if (!jobId) {
      throw new Error('Job ID is required to save job requirements');
    }

    console.log('Saving edited job requirements:', editedData);

    // Make API call to update job requirements
    const response = await fetch(API_ENDPOINTS.UPDATE_JOB_JR, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      },
      body: JSON.stringify({
        job_id: jobId,
        job_requirements: editedData
      })
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Job requirements updated successfully:', result);

    // Reload the page to refresh data
    window.location.reload();
  };
  
  if (!jobRequirement) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600">No job requirements data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tab-related header */}
      <div className="bg-gray-100 shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
        <div className="flex justify-between items-start">
          <div className="mr-8">
            <h2 className="text-2xl font-bold text-gray-900">Job Requirements</h2>
            <p className="text-gray-600 mt-2">
              This tab displays the requirements for this job position, including skills, expertise, and candidate qualifications.
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={() => setIsRawEditOpen(true)}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors whitespace-nowrap flex items-center"
            >
              <FaFileCode className="mr-2" /> Raw JSON Edit
            </button>
            {user?.role === 'Superadmin' && (
              <button
                onClick={() => console.log("Regenerate Job Requirements clicked")}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors whitespace-nowrap flex items-center"
              >
                <FiRefreshCw className="mr-2" /> Regenerate Job Requirements
              </button>
            )}
          </div>
        </div>
      </div>
      
      {jdPDF ? (
        // Two-column layout with PDF and job requirements
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          {/* Original Job Description PDF - 33% width */}
          <div className="md:w-1/3 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">
              Original Job Description
            </h3>
            <div className="h-[600px] overflow-hidden">
              <iframe 
                src={jdPDF.url} 
                title={jdPDF.filename || "Original Job Description"} 
                className="w-full h-full border-0"
              />
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {jdPDF.filename && <p>Filename: {jdPDF.filename}</p>}
            </div>
          </div>
          
          {/* Inferred Job Requirements - 66% width */}
          <div className="md:w-2/3">
            <JobRequirementsContent jobRequirement={jobRequirement} />
          </div>
        </div>
      ) : (
        // If no PDF is available, show the job requirements at full width
        <JobRequirementsContent jobRequirement={jobRequirement} />
      )}
      
      {/* Raw Edit Modal */}
      <RawJsonEditor
        isOpen={isRawEditOpen}
        onClose={() => setIsRawEditOpen(false)}
        data={jobRequirement}
        title="Raw Edit Job Requirements"
        description="Edit the job requirements JSON directly. This is a temporary solution which will be replaced with a form."
        rootName="jobRequirement"
        hiddenAttributes={['rationale', 'relevance']}
        onSave={handleSaveJobRequirements}
        saveButtonText="Save"
      />
    </div>
  );
};

export default JobRequirements;
